import type { Breadcrumb, ErrorEvent } from "@sentry/react";

// Keeps personal data out of the error monitor (GDPR): everything it receives is data held by
// another company. The server does the same in App\Service\Monitoring\Redactor.

const PATTERNS: [RegExp, string][] = [
  [/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, "[token]"],
  [/([?&](?:token|code|key|secret|password|next)=)[^&\s#"']+/gi, "$1[redacted]"],
  [/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]"],
  [/\b[a-f0-9]{40,}\b/gi, "[token]"],
];

const SENSITIVE_KEY = /authorization|cookie|passw|secret|token|email|phone|address|signature/i;

export function redactText(text: string): string {
  return PATTERNS.reduce((out, [pattern, replacement]) => out.replace(pattern, replacement), text);
}

function redactDeep<T>(value: T): T {
  if (typeof value === "string") return redactText(value) as T;
  if (Array.isArray(value)) return value.map(redactDeep) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        SENSITIVE_KEY.test(key) && (item === null || typeof item !== "object") ? "[redacted]" : redactDeep(item),
      ]),
    ) as T;
  }
  return value;
}

/** Sentry `beforeSend`: no user, no cookies, and no email or token in any text of the event. */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  delete event.user;
  if (event.message) event.message = redactText(event.message);
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) exception.value = redactText(exception.value);
  }
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    if (event.request.url) event.request.url = redactText(event.request.url);
    if (event.request.query_string) event.request.query_string = redactDeep(event.request.query_string);
    if (event.request.headers) event.request.headers = redactDeep(event.request.headers);
  }
  if (event.extra) event.extra = redactDeep(event.extra);
  if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  return event;
}

/** Sentry `beforeBreadcrumb`: the trail of requests and clicks leading to an error. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.message) breadcrumb.message = redactText(breadcrumb.message);
  if (breadcrumb.data) breadcrumb.data = redactDeep(breadcrumb.data);
  return breadcrumb;
}
